import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import {
  authAPI,
  CartResponse,
  formatPriceCents,
  getUserErrorMessage,
  hasActiveSubscription,
  MarketplaceListingDetail,
  MarketplaceListingSummary,
  MarketplaceOrder,
  marketplaceAPI,
  User,
} from '../api/api';
import '../styles/marketplace.css';

type Tab = 'browse' | 'sell' | 'mine' | 'cart' | 'orders';

const TAB_ROUTES: Record<Tab, string> = {
  browse: '/marketplace/browse',
  sell: '/marketplace/sell',
  mine: '/marketplace/listed',
  cart: '/marketplace/cart',
  orders: '/marketplace/orders',
};

const VALID_SECTIONS = new Set(['browse', 'listed', 'sell', 'cart', 'orders']);

function tabFromSection(section: string | undefined): Tab {
  switch (section) {
    case 'listed':
      return 'mine';
    case 'sell':
      return 'sell';
    case 'cart':
      return 'cart';
    case 'orders':
      return 'orders';
    case 'browse':
    default:
      return 'browse';
  }
}

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return value;
  }
}

const Marketplace: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { section, listingId: listingIdParam } = useParams<{ section?: string; listingId?: string }>();
  const tab = tabFromSection(section);
  const selectedId = useMemo(() => {
    if (!listingIdParam) {
      return null;
    }
    const id = Number(listingIdParam);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [listingIdParam]);

  const [me, setMe] = useState<User | null>(null);
  const [loadingMe, setLoadingMe] = useState(true);
  const [listings, setListings] = useState<MarketplaceListingSummary[]>([]);
  const [myListings, setMyListings] = useState<MarketplaceListingSummary[]>([]);
  const [selected, setSelected] = useState<MarketplaceListingDetail | null>(null);
  const [cart, setCart] = useState<CartResponse | null>(null);
  const [orders, setOrders] = useState<MarketplaceOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceDollars, setPriceDollars] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const isSubscriber = useMemo(() => hasActiveSubscription(me), [me]);

  const marketplaceTabs = useMemo((): [Tab, string][] => {
    const tabs: [Tab, string][] = [['browse', 'Browse']];
    if (isSubscriber) {
      tabs.push(
        ['sell', 'List an item'],
        ['mine', 'My listings'],
        ['cart', 'Cart'],
        ['orders', 'Orders']
      );
    }
    return tabs;
  }, [isSubscriber]);

  const clearFlash = () => {
    setError('');
    setSuccess('');
  };

  const loadBrowse = useCallback(() => {
    setLoading(true);
    clearFlash();
    marketplaceAPI
      .listListings({ status: 'active', limit: 48 })
      .then((res) => setListings(res.items))
      .catch((err: unknown) => {
        setError(getUserErrorMessage(err, 'Could not load marketplace listings.'));
      })
      .finally(() => setLoading(false));
  }, []);

  const loadMine = useCallback(() => {
    setLoading(true);
    clearFlash();
    marketplaceAPI
      .myListings()
      .then((res) => setMyListings(res.items))
      .catch((err: unknown) => {
        setError(getUserErrorMessage(err, 'Could not load your listings.'));
      })
      .finally(() => setLoading(false));
  }, []);

  const loadCart = useCallback(() => {
    setLoading(true);
    clearFlash();
    marketplaceAPI
      .getCart()
      .then(setCart)
      .catch((err: unknown) => {
        setError(getUserErrorMessage(err, 'Could not load your cart.'));
      })
      .finally(() => setLoading(false));
  }, []);

  const loadOrders = useCallback(() => {
    setLoading(true);
    clearFlash();
    marketplaceAPI
      .listOrders('all')
      .then((res) => setOrders(res.items))
      .catch((err: unknown) => {
        setError(getUserErrorMessage(err, 'Could not load orders.'));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    authAPI
      .getMe()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setLoadingMe(false));
  }, []);

  useEffect(() => {
    if (listingIdParam) {
      return;
    }
    if (section && !VALID_SECTIONS.has(section)) {
      navigate('/marketplace/browse', { replace: true });
    }
  }, [section, listingIdParam, navigate]);

  useEffect(() => {
    if (!me) {
      return;
    }
    if (!isSubscriber && tab !== 'browse') {
      navigate(TAB_ROUTES.browse, { replace: true });
      return;
    }
    if (selectedId != null) {
      return;
    }
    if (tab === 'browse') {
      loadBrowse();
    } else if (tab === 'mine') {
      loadMine();
    } else if (tab === 'cart') {
      loadCart();
    } else if (tab === 'orders') {
      loadOrders();
    }
  }, [me, isSubscriber, tab, selectedId, navigate, loadBrowse, loadMine, loadCart, loadOrders]);

  useEffect(() => {
    if (selectedId == null || !me) {
      setSelected(null);
      return;
    }
    setLoading(true);
    clearFlash();
    marketplaceAPI
      .getListing(selectedId)
      .then(setSelected)
      .catch((err: unknown) => {
        setError(getUserErrorMessage(err, 'Could not load listing.'));
        navigate('/marketplace/browse', { replace: true });
      })
      .finally(() => setLoading(false));
  }, [selectedId, me, navigate]);

  useEffect(() => {
    const urls = photos.map((file) => URL.createObjectURL(file));
    setPhotoPreviews(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [photos]);

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    clearFlash();
    if (photos.length === 0) {
      setError('Add at least one photo of the item.');
      return;
    }
    const dollars = Number(priceDollars);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      setError('Enter a valid price in dollars.');
      return;
    }
    const priceCents = Math.round(dollars * 100);
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('price_cents', String(priceCents));
      formData.append('status', 'active');
      photos.forEach((file) => formData.append('images', file));
      const created = await marketplaceAPI.createListing(formData);
      setSuccess(`Listed “${created.title}”.`);
      setTitle('');
      setDescription('');
      setPriceDollars('');
      setPhotos([]);
      setSelected(created);
      loadMine();
      loadBrowse();
      navigate(`/marketplace/listings/${created.id}`, { state: { from: TAB_ROUTES.mine } });
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Could not create listing.'));
    } finally {
      setCreating(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selected) {
      return;
    }
    setActionBusy(true);
    clearFlash();
    try {
      await marketplaceAPI.addToCart(selected.id);
      setSuccess('Added to cart.');
      navigate(TAB_ROUTES.cart);
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Could not add to cart.'));
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeleteListing = async (id: number) => {
    setActionBusy(true);
    clearFlash();
    try {
      await marketplaceAPI.deleteListing(id);
      setSuccess('Listing deleted.');
      setSelected(null);
      navigate(TAB_ROUTES.mine);
      loadMine();
      loadBrowse();
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Could not delete listing.'));
    } finally {
      setActionBusy(false);
    }
  };

  const handleCheckout = async () => {
    setActionBusy(true);
    clearFlash();
    try {
      const result = await marketplaceAPI.checkout();
      setSuccess(`Created ${result.total} order(s). Complete escrow to finalize.`);
      navigate(TAB_ROUTES.orders);
      loadOrders();
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Checkout failed.'));
    } finally {
      setActionBusy(false);
    }
  };

  const runOrderAction = async (
    action: () => Promise<MarketplaceOrder>,
    message: string
  ) => {
    setActionBusy(true);
    clearFlash();
    try {
      await action();
      setSuccess(message);
      loadOrders();
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Order action failed.'));
    } finally {
      setActionBusy(false);
    }
  };

  if (loadingMe) {
    return (
      <div className="container marketplace-page">
        <p className="marketplace-empty">Loading marketplace…</p>
      </div>
    );
  }

  if (!me) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="container marketplace-page"
      >
        <div className="card marketplace-intro">
          <p className="marketplace-eyebrow">Members marketplace</p>
          <h1>Marketplace</h1>
          <p>Sign in to browse community listings and prices.</p>
        </div>
        <div className="card marketplace-paywall">
          <h2>Sign in required</h2>
          <p>Log in to view marketplace items. An active subscription is required to buy or list.</p>
          <Link to="/login" className="btn btn-primary">
            Log in
          </Link>
        </div>
      </motion.div>
    );
  }

  const subscriberPaywall = (
    <div className="card marketplace-paywall">
      <h2>Subscription required</h2>
      <p>
        Browse listings for free. Subscribe to add items to your cart, list your own photos, and
        complete orders through escrow.
      </p>
      <Link to="/payment" className="btn btn-primary">
        Subscribe now
      </Link>
    </div>
  );

  const renderCard = (item: MarketplaceListingSummary) => (
    <button
      key={item.id}
      type="button"
      className="marketplace-card"
      onClick={() => {
        clearFlash();
        navigate(`/marketplace/listings/${item.id}`, { state: { from: TAB_ROUTES[tab] } });
      }}
    >
      {item.cover_image_url ? (
        <img className="marketplace-card-image" src={item.cover_image_url} alt={item.title} />
      ) : (
        <div className="marketplace-card-placeholder">No photo</div>
      )}
      <div className="marketplace-card-body">
        <h3>{item.title}</h3>
        <p className="marketplace-price">{formatPriceCents(item.price_cents)}</p>
        <p className="marketplace-meta">
          {item.seller.name} · {item.image_count} photo{item.image_count === 1 ? '' : 's'}
        </p>
        {item.status !== 'active' && <span className="marketplace-status">{item.status}</span>}
      </div>
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="container marketplace-page"
    >
      <div className="card marketplace-intro">
        <p className="marketplace-eyebrow">Members marketplace</p>
        <h1>Marketplace</h1>
        <p>
          Browse every listing and price. Subscribers can buy through escrow and list items with
          photos.
        </p>
      </div>

      {!isSubscriber && subscriberPaywall}

      {error && (
        <div className="error-message marketplace-banner" role="alert">
          {error}
        </div>
      )}
      {success && <div className="marketplace-success">{success}</div>}

      {selectedId != null ? (
        <div className="card">
          <button
            type="button"
            className="marketplace-back"
            onClick={() => {
              setSelected(null);
              const from = (location.state as { from?: string } | null)?.from;
              navigate(from || TAB_ROUTES.browse);
            }}
          >
            ← Back to marketplace
          </button>
          {!selected ? (
            <p className="marketplace-empty">Loading listing…</p>
          ) : (
          <div className="marketplace-detail">
            <div className="marketplace-gallery">
              {selected.images.length === 0 ? (
                <div className="marketplace-card-placeholder">No photos</div>
              ) : (
                selected.images.map((image) => (
                  <img
                    key={image.id}
                    className="marketplace-detail-image"
                    src={image.url}
                    alt={`${selected.title} ${image.sort_order + 1}`}
                  />
                ))
              )}
            </div>
            <div>
              <h2>{selected.title}</h2>
              <p className="marketplace-price">{formatPriceCents(selected.price_cents)}</p>
              <p className="marketplace-meta">
                Seller: {selected.seller.name} · Listed {formatDate(selected.created_at)}
              </p>
              <span className="marketplace-status">{selected.status}</span>
              <p style={{ marginTop: 16, lineHeight: 1.7, color: 'var(--text-secondary)' }}>
                {selected.description}
              </p>
              <div className="marketplace-detail-actions">
                {selected.status === 'active' && selected.seller.id !== me?.id && (
                  isSubscriber ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={actionBusy}
                      onClick={handleAddToCart}
                    >
                      Add to cart
                    </button>
                  ) : (
                    <Link to="/payment" className="btn btn-primary">
                      Subscribe to buy
                    </Link>
                  )
                )}
                {(selected.seller.id === me?.id || me?.is_admin) && selected.status !== 'reserved' && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={actionBusy}
                    onClick={() => handleDeleteListing(selected.id)}
                  >
                    Delete listing
                  </button>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      ) : (
        <>
          <div className="marketplace-tabs" role="tablist">
            {marketplaceTabs.map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                className={`marketplace-tab ${tab === id ? 'active' : ''}`}
                onClick={() => {
                  clearFlash();
                  navigate(TAB_ROUTES[id]);
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'browse' && (
            <div>
              {loading && <p className="marketplace-empty">Loading listings…</p>}
              {!loading && listings.length === 0 && (
                <p className="marketplace-empty">No active listings yet. Be the first to list an item.</p>
              )}
              <div className="marketplace-grid">{listings.map(renderCard)}</div>
            </div>
          )}

          {tab === 'sell' && (
            <div className="card">
              <h2 style={{ color: 'var(--neon-magenta)', marginBottom: 16 }}>List an item by picture</h2>
              <form className="marketplace-form" onSubmit={handleCreate}>
                <div className="form-group">
                  <label htmlFor="marketplace-photos">Photos (required, up to 8)</label>
                  <input
                    id="marketplace-photos"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/*"
                    capture="environment"
                    multiple
                    onChange={(event) => {
                      const files = Array.from(event.target.files || []).slice(0, 8);
                      setPhotos(files);
                    }}
                  />
                  {photoPreviews.length > 0 && (
                    <div className="marketplace-preview-row">
                      {photoPreviews.map((url) => (
                        <img key={url} className="marketplace-preview" src={url} alt="Upload preview" />
                      ))}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="marketplace-title">Title</label>
                  <input
                    id="marketplace-title"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                    required
                    minLength={3}
                    maxLength={200}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="marketplace-price">Price (USD)</label>
                  <input
                    id="marketplace-price"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={priceDollars}
                    onChange={(event) => setPriceDollars(event.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="marketplace-description">Description</label>
                  <textarea
                    id="marketplace-description"
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    required
                    minLength={10}
                    maxLength={10000}
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Publishing…' : 'Publish listing'}
                </button>
              </form>
            </div>
          )}

          {tab === 'mine' && (
            <div>
              {loading && <p className="marketplace-empty">Loading your listings…</p>}
              {!loading && myListings.length === 0 && (
                <p className="marketplace-empty">You have not listed anything yet.</p>
              )}
              <div className="marketplace-grid">{myListings.map(renderCard)}</div>
            </div>
          )}

          {tab === 'cart' && (
            <div className="card">
              <h2 style={{ color: 'var(--neon-magenta)', marginBottom: 16 }}>Your cart</h2>
              {loading && <p className="marketplace-empty">Loading cart…</p>}
              {!loading && (!cart || cart.items.length === 0) && (
                <p className="marketplace-empty">Cart is empty.</p>
              )}
              {cart && cart.items.length > 0 && (
                <>
                  <div className="marketplace-orders">
                    {cart.items.map((item) => (
                      <div key={item.id} className="marketplace-order card" style={{ margin: 0 }}>
                        {item.listing.cover_image_url ? (
                          <img
                            className="marketplace-order-thumb"
                            src={item.listing.cover_image_url}
                            alt={item.listing.title}
                          />
                        ) : (
                          <div className="marketplace-card-placeholder" style={{ aspectRatio: '1', height: 72 }}>
                            —
                          </div>
                        )}
                        <div>
                          <strong>{item.listing.title}</strong>
                          <p className="marketplace-price">{formatPriceCents(item.listing.price_cents)}</p>
                        </div>
                        <div className="marketplace-order-actions">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={actionBusy}
                            onClick={async () => {
                              setActionBusy(true);
                              try {
                                await marketplaceAPI.removeFromCart(item.id);
                                loadCart();
                              } catch (err: unknown) {
                                setError(getUserErrorMessage(err, 'Could not remove item.'));
                              } finally {
                                setActionBusy(false);
                              }
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p style={{ marginTop: 20 }}>
                    Total: <strong className="marketplace-price">{formatPriceCents(cart.total_cents)}</strong>
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ marginTop: 12 }}
                    disabled={actionBusy}
                    onClick={handleCheckout}
                  >
                    Checkout
                  </button>
                </>
              )}
            </div>
          )}

          {tab === 'orders' && (
            <div className="card">
              <h2 style={{ color: 'var(--neon-magenta)', marginBottom: 16 }}>Orders</h2>
              {loading && <p className="marketplace-empty">Loading orders…</p>}
              {!loading && orders.length === 0 && (
                <p className="marketplace-empty">No orders yet.</p>
              )}
              <div className="marketplace-orders">
                {orders.map((order) => {
                  const isBuyer = order.buyer_id === me?.id;
                  const isSeller = order.seller_id === me?.id;
                  return (
                    <div key={order.id} className="marketplace-order card" style={{ margin: 0 }}>
                      {order.cover_image_url ? (
                        <img
                          className="marketplace-order-thumb"
                          src={order.cover_image_url}
                          alt={order.listing_title}
                        />
                      ) : (
                        <div className="marketplace-card-placeholder" style={{ aspectRatio: '1', height: 72 }}>
                          —
                        </div>
                      )}
                      <div>
                        <strong>{order.listing_title}</strong>
                        <p className="marketplace-price">{formatPriceCents(order.price_cents)}</p>
                        <p className="marketplace-meta">
                          Buyer: {order.buyer_name} · Seller: {order.seller_name}
                        </p>
                        <span className="marketplace-status">{order.status}</span>
                      </div>
                      <div className="marketplace-order-actions">
                        {isBuyer && order.status === 'pending_payment' && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={actionBusy}
                            onClick={() =>
                              runOrderAction(
                                () => marketplaceAPI.recordEscrow(order.id),
                                'Escrow recorded. Waiting for seller to ship.'
                              )
                            }
                          >
                            Record escrow
                          </button>
                        )}
                        {isSeller && order.status === 'escrowed' && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={actionBusy}
                            onClick={() =>
                              runOrderAction(
                                () => marketplaceAPI.markShipped(order.id),
                                'Marked as shipped.'
                              )
                            }
                          >
                            Mark shipped
                          </button>
                        )}
                        {isBuyer && order.status === 'shipped' && (
                          <>
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={actionBusy}
                              onClick={() =>
                                runOrderAction(
                                  () => marketplaceAPI.confirmDelivery(order.id),
                                  'Delivery confirmed. Seller can redeem the claim.'
                                )
                              }
                            >
                              Confirm received
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={actionBusy}
                              onClick={() =>
                                runOrderAction(
                                  () => marketplaceAPI.burnOrder(order.id),
                                  'Claim burned. Escrow refunded to buyer.'
                                )
                              }
                            >
                              Not as described
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default Marketplace;
